
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePropertyTypes } from "@/hooks/usePropertyTypes";
import { PropertyType } from "@/lib/types"; // Assuming PropertyType type exists
import { useEffect, useMemo, useState } from "react"; // Add useState
import { Badge } from "@/components/ui/badge"; // Import Badge
import { X } from "lucide-react"; // Import X icon for remove button

interface PropertyTypeFilterProps {
  selectedTypes: string[];
  onToggleType: (type: string) => void;
  onClearTypes: () => void;
}

const PropertyTypeFilter = ({
  selectedTypes,
  onToggleType,
  onClearTypes,
}: PropertyTypeFilterProps) => {
  const { propertyTypes = [], isLoading } = usePropertyTypes();
  const [isOpen, setIsOpen] = useState(false);

  // Log the raw data when it loads
  useEffect(() => {
    if (propertyTypes && propertyTypes.length > 0) {
      console.log(
        "[PropertyTypeFilter] Raw propertyTypes data:",
        propertyTypes
      );
    }
  }, [propertyTypes]);

  // Filter for valid items AND unique names
  const uniqueValidPropertyTypes = useMemo(() => {
    // Wrap in useMemo
    const seenNames = new Set<string>();
    return (propertyTypes || []).filter(
      (type: PropertyType | null | undefined): type is PropertyType => {
        const isValid = !!(
          type &&
          type.id != null &&
          type.name &&
          typeof type.name === "string" &&
          type.name.trim() !== ""
        );
        if (!isValid && type) {
          console.warn(
            "[PropertyTypeFilter] Filtering out invalid property type item:",
            type
          );
          return false;
        }
        if (isValid && !seenNames.has(type.name)) {
          seenNames.add(type.name);
          return true; // Keep valid and unique name
        }
        if (isValid && seenNames.has(type.name)) {
          console.warn(
            "[PropertyTypeFilter] Filtering out duplicate property type name:",
            type
          );
        }
        return false; // Filter out duplicates
      }
    );
  }, [propertyTypes]); // Recompute when data changes

  // Handle selecting 'all' which clears the selection
  const handleTypeSelect = (value: string) => {
    if (value === "all") {
      onClearTypes();
    } else {
      onToggleType(value);
      setIsOpen(false); // Keep dropdown open for multiple selections
    }
  };

  // Get display names for selected types
  const getTypeDisplayName = (typeName: string) => {
    const type = uniqueValidPropertyTypes.find(t => t.name === typeName);
    return type?.display_name || type?.name || typeName;
  };

  return (
    <div className="p-4 border-b border-gray-200">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-medium text-sm text-gray-500">PROPERTY TYPE</h3>
        {selectedTypes.length > 0 && (
          <button
            onClick={onClearTypes}
            className="text-xs text-propertylk-blue hover:underline"
          >
            Clear
          </button>
        )}
      </div>

      {/* Display selected types as badges */}
      {selectedTypes.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {selectedTypes.map((type) => (
            <Badge 
              key={type}
              variant="secondary" 
              className="pl-2 pr-1 py-0.5 text-sm bg-blue-100 text-blue-800 border border-blue-200"
            >
              {getTypeDisplayName(type)}
              <button
                onClick={() => onToggleType(type)}
                className="ml-1 p-0.5 rounded-full hover:bg-blue-300"
                aria-label={`Remove ${type} filter`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      
      <Select
        onValueChange={handleTypeSelect}
        value={selectedTypes.length === 0 ? "all" : undefined}
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <SelectTrigger 
          className="w-full mb-3"
          onClick={() => setIsOpen(true)}
        >
          <SelectValue 
            placeholder={selectedTypes.length > 0 
              ? `${selectedTypes.length} type${selectedTypes.length > 1 ? 's' : ''} selected` 
              : "All Property Types"} 
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Property Types</SelectItem>
          {/* Map over unique valid list */}
          {uniqueValidPropertyTypes.map((type) => {
            // Basic check before rendering item
            if (!type || type.id == null || type.name === "") {
              console.error(
                "[PropertyTypeFilter] Skipping render for invalid item inside map:",
                type
              );
              return null;
            }
            return (
              <SelectItem key={type.id} value={type.name || `type-${type.id}`}>
                {type.display_name || type.name || "Unknown Type"}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};

export default PropertyTypeFilter;
